import { activateUserSession, getFlexColDomainValueForCode, isEmpty, isFormValid, setError } from '../../../../library/Core/SmartFunctions';
import { ControlOnChangeArguments, DispatchEvent, State } from '../../../../library/Core/SmartTypes';
import logger from '../../../../library/Core/logger';
import { axiosClient } from '../../../../library/Service/axiosClient';
import { getDomainElementsForGivenYearRange } from '../../../../library/Service/domainService';
import ScreenNavigationService, { AlumniProfileEditScreens } from '../../../../services/screen-navigation.service';
import StudentService from '../../../../services/student.service';
import SessionContext from '../../../../library/Core/SessionContext';
import axios from 'axios';
import { navigateToNextStep } from '../../services/profile-next-step-navigator';
import e from 'express';
import { set } from 'date-fns';


const getProfileUserType = (sessionUserType: string | undefined, data: any) => {
  if (sessionUserType === 'SUPER_ADMIN' || sessionUserType === 'INSTITUTE_ADMIN') {
    return data?.selectedInstitute?.userTypeCode || data?.userTypeCode || sessionUserType;
  }
  return sessionUserType;
};

export const getProgramDurationInYears = (programId: any, state: any): number | null => {
  const flexColStr = getFlexColDomainValueForCode(programId, 'ALL_PROGRAM_CODE', state);
  if (isEmpty(flexColStr)) return null;

  try {
    const flex = typeof flexColStr === 'string' ? JSON.parse(flexColStr) : flexColStr;
    const durationCount = parseFloat(flex?.prog_duration_desc);
    if (isNaN(durationCount)) return null;

    let durationYears = 0;
    switch (flex?.prog_duration) {
      case 'DAYS':
        durationYears = durationCount / 365;
        break;
      case 'WEEKS':
        durationYears = durationCount / 52;
        break;
      case 'MONTHS':
        durationYears = durationCount / 12;
        break;
      case 'YEARS':
        durationYears = durationCount;
        break;
      default:
        return null;
    }

    return durationYears > 0 ? durationYears : null;
  } catch {
    return null;
  }
};

export const getStudentStartYearMin = (durationYears: number, currentYear: number): number => {
  const rounded = Math.round(durationYears);
  const yearsBack = rounded <= 1 ? 1 : rounded - 1;
  return currentYear - yearsBack;
};

export const applyStudentStartYearDomain = (domain: Map<string, any>, data: any, sessionState: any) => {
  const userType = getProfileUserType(sessionState?.userTypeCode, data);
  if (userType !== 'STUDENT') return domain;

  const currentYear = new Date().getFullYear();
  const durationYears = getProgramDurationInYears(data?.selectedInstitute?.programId, { domain } as State);
  if (durationYears == null) return domain;

  let minYear = getStudentStartYearMin(durationYears, currentYear);
  const foundingYear = parseInt(data?.selectedInstitute?.instituteFoundingYear);
  if (!isNaN(foundingYear)) minYear = Math.max(minYear, foundingYear);

  domain.set(
    'INSTITUTE_START_YEAR_CODE',
    getDomainElementsForGivenYearRange('INSTITUTE_START_YEAR_CODE', minYear, currentYear)
  );
  return domain;
};

export const onStartOrEndYearChange = (
  props: ControlOnChangeArguments,
  sessionState: any,
  programLevel: any
) => {

  const currentYear = new Date().getFullYear();
  const userType = getProfileUserType(
    props.state.actions['sessionState'].userTypeCode,
    props.state.data
  );

  let programLevelValue = props.state.actions['sessionState'].programLevel;

  if (props.state.actions['sessionState'].userTypeCode === 'SUPER_ADMIN' || props.state.actions['sessionState'].userTypeCode === 'INSTITUTE_ADMIN') {
    programLevelValue = programLevel;
  }

  const startYear =
    props.control.id === 'startYear'
      ? parseInt(props.value)
      : parseInt(props.state.data?.selectedInstitute?.startYear);

  if (props.control.id !== 'startYear') return;

  if (!startYear || isNaN(startYear)) {
    clearCompletion(props);
    setError(
      'selectedInstitute.startYear',
      ['Start year is required'],
      props.dispatch
    );
    return;
  }

  // ===== CALCULATE RANGE =====
  let minCompletionYear = startYear;
  let maxCompletionYear = startYear;

  const durationYears = getProgramDurationInYears(props.state.data?.selectedInstitute?.programId, props.state);
  const isSubYearDuration = durationYears != null && durationYears < 1;
  // Sub-year programs (e.g. 9 months) can finish in the same calendar year.
  let computedEndYear: number | null =
    durationYears != null ? startYear + (isSubYearDuration ? 0 : Math.round(durationYears)) : null;

  // Keep the full selectable range (based on program level) so outliers (e.g. dropouts
  // who took a year or two extra) can still pick a different completion year. The
  // computed duration-based year (if available) is only used to auto-select a default below.
  switch (programLevelValue) {
    case 'UG':
      minCompletionYear = startYear + 2;
      maxCompletionYear = startYear + 6;
      break;
    case 'PG':
      minCompletionYear = startYear + 1;
      maxCompletionYear = startYear + 4;
      break;
    case 'DOCTORAL':
      minCompletionYear = startYear + 3;
      maxCompletionYear = startYear + 7;
      break;
    default:
      minCompletionYear = startYear + 1;
      maxCompletionYear = startYear + 1;
  }

  if (isSubYearDuration) {
    minCompletionYear = Math.min(minCompletionYear, startYear);
    maxCompletionYear = Math.max(maxCompletionYear, startYear + 1);
  }

  // Make sure the computed year is included in the selectable range even if it
  // falls outside the default program-level range.
  if (computedEndYear !== null) {
    minCompletionYear = Math.min(minCompletionYear, computedEndYear);
    maxCompletionYear = Math.max(maxCompletionYear, computedEndYear);
  }

  if (userType === 'ALUMNI') {
    maxCompletionYear = Math.min(maxCompletionYear, currentYear);
  }

  if (userType === 'STUDENT') {
    minCompletionYear = Math.max(minCompletionYear, currentYear);
  }

  // Students currently enrolled cannot complete in the past. If duration maps to
  // the start year (common for < 1 year programs started in Dec last year), use current year.
  let autoSelectYear = computedEndYear;
  if (userType === 'STUDENT' && autoSelectYear != null) {
    autoSelectYear = Math.max(autoSelectYear, currentYear);
  }

  // reset the pass out year if it's out of range
  if (minCompletionYear > maxCompletionYear) {
    clearCompletion(props);
  } else {
    const newDomain = getDomainElementsForGivenYearRange(
      'INSTITUTE_PASS_OUT_YEAR_CODE',
      minCompletionYear,
      maxCompletionYear
    );

    props.dispatch({
      type: 'SET_DOMAIN',
      payload: {
        key: 'INSTITUTE_PASS_OUT_YEAR_CODE',
        value: newDomain,
      },
    });

    props.dispatch({
      type: 'CONTROL_VALUE_CHANGE',
      payload: {
        dataKey: 'selectedInstitute.passOutYear',
        value:
          autoSelectYear !== null && autoSelectYear >= minCompletionYear && autoSelectYear <= maxCompletionYear
            ? autoSelectYear
            : userType === 'STUDENT' && currentYear >= minCompletionYear && currentYear <= maxCompletionYear
              ? currentYear
              : '',
      },
    });
  }

  // ===== NOW VALIDATION (AFTER DOMAIN UPDATE) =====

  if (userType === 'ALUMNI' && startYear >= currentYear) {
    setError(
      'selectedInstitute.startYear',
      ['Alumni start year must be less than current year'],
      props.dispatch
    );
  }

};



    // ===== Utility: Clear Completion =====
    const clearCompletion = (props: any) => {
        props.dispatch({
            type: 'CONTROL_VALUE_CHANGE',
            payload: {
            dataKey: 'selectedInstitute.passOutYear',
            value: '',
            },
        });

        props.dispatch({
            type: 'SET_DOMAIN',
            payload: {
            key: 'INSTITUTE_PASS_OUT_YEAR_CODE',
            value: [],
            },
        });
    };



// export const onStartOrEndYearChange = (props: ControlOnChangeArguments, sessionState: any, programLevel: any) => {
//     const errorMessages = [] as any[];
//     if (props.control.id === 'startYear') {


//         let programlevelValue = props.state.actions['sessionState'].programLevel;
//         if (
//             props.state.actions['sessionState'].userTypeCode === 'SUPER_ADMIN' ||
//             props.state.actions['sessionState'].userTypeCode === 'INSTITUTE_ADMIN'
//         ) {
//             programlevelValue = programLevel;
//         }

//         const currentDate = new Date();
//         const currentYear = currentDate.getFullYear();
//         let endYear: any = currentYear;
//         let startYear = props.value;

//         if (programlevelValue === 'UG') {
//             endYear = parseInt(props.value) + 5;
//             startYear = parseInt(props.value) + 3;
//         } else if (programlevelValue === 'PG') {
//             endYear = parseInt(props.value) + 3;
//             startYear = parseInt(props.value) + 1;
//         } else if (programlevelValue === 'DOCTORAL') {
//             endYear = parseInt(props.value) + 6;
//             startYear = parseInt(props.value) + 3;
//         } else {
//             endYear = parseInt(props.value) + 1;
//             startYear = parseInt(props.value) + 1;
//         }
        
//         alert('Start year changed: ' + props.value + ' calculated end year: ' + endYear + ' program level: ' + programlevelValue);
//         if (props.state.actions['sessionState'].userTypeCode == 'ALUMNI' && endYear > currentYear) {
//             // also check if start year is not blank and end year is greater than current year then only show error
//             alert('end year' + endYear + 'current year' + currentYear);
//                 if(props.value != ''){
                    
//                     errorMessages.push('Please select valid start year');
//                     setError('selectedInstitute.startYear', ['Please select valid start year'], props.dispatch);
//                 } else if(props.value != '' && endYear > currentYear){
//                     errorMessages.push('End year cannot be greater than current year');
//                     setError('selectedInstitute.startYear', ['End year cannot be greater than current year'], props.dispatch);
//                 } else {
//                     setError('selectedInstitute.startYear', [], props.dispatch);
//                     endYear = currentYear;
//                 }
//         }

//         props.dispatch({
//             type: 'SET_DOMAIN',
//             payload: {
//                 key: 'INSTITUTE_PASS_OUT_YEAR_CODE',
//                 value: getDomainElementsForGivenYearRange('INSTITUTE_PASS_OUT_YEAR_CODE', parseInt(startYear), endYear),
//             },
            
//         });

//         props.dispatch({
//             type: 'CONTROL_VALUE_CHANGE',
//             payload: {
//                 dataKey: 'selectedInstitute.passOutYear',
//                 value: parseInt(endYear),
//             },
//         });
//         alert('Start year changed, end year domain updated'+props.state.data?.selectedInstitute?.startYear);

//         // set error if end year or start year is empty
//         if (props.state.data?.selectedInstitute?.startYear === '' || props.state.data?.selectedInstitute?.passOutYear === '') {
//             errorMessages.push('Please select start year and end year');
//             props.dispatch({
//                 type: 'SET_FIELD_VALIDATION_ERRORS',
//                 payload: { dataKey: 'selectedInstitute.startYear', errorMessages: ['Please select start year and end year']  },
//             });
//         }
//     }
// };

const isAggreeToUnivariety = (state: State, dispatch: (dispatchEvent: DispatchEvent) => void) => {
    
    if (state?.data?.user?.agreeToUnivariety === false || state?.data?.user?.agreeToUnivariety === undefined) {
        setError('user.agreeToUnivariety', ['Please agree to univariety'], dispatch);
        dispatch({ type: 'SHOW_ERRORS' });
        return true;
    }

    return false;
};

const validateAcademicYears = (
  state: State,
  dispatch: (dispatchEvent: DispatchEvent) => void
): boolean => {

    let isValid = true;

    const currentYear = new Date().getFullYear();
    const userType = getProfileUserType(state?.actions?.sessionState?.userTypeCode, state?.data);

    const startYear = state?.data?.selectedInstitute?.startYear;
    const passOutYear = state?.data?.selectedInstitute?.passOutYear;

    // Clear previous errors
    setError('selectedInstitute.startYear', [], dispatch);
    setError('selectedInstitute.passOutYear', [], dispatch);

    // Required validation
    if (isEmpty(startYear)) {
        isValid = false;
        setError(
        'selectedInstitute.startYear',
        ['Start year is required'],
        dispatch
        );
    }

    if (isEmpty(passOutYear)) {
        isValid = false;
        setError(
        'selectedInstitute.passOutYear',
        ['Completion year is required'],
        dispatch
        );
    }

    if (!isValid) return false;

    const start = parseInt(startYear);
    const end = parseInt(passOutYear);

    // Completion before start
    if (end < start) {
        isValid = false;
        setError(
        'selectedInstitute.passOutYear',
        ['Completion year cannot be before start year'],
        dispatch
        );
    }

    // Alumni rules
    if (userType === 'ALUMNI') {

        if (start >= currentYear) {
        isValid = false;
        setError(
            'selectedInstitute.startYear',
            ['Alumni start year must be less than current year'],
            dispatch
        );
        }

        if (end > currentYear) {
        isValid = false;
        setError(
            'selectedInstitute.passOutYear',
            ['Alumni completion year cannot be in future'],
            dispatch
        );
        }
    }

    // Student rules
    if (userType === 'STUDENT') {
        if (end < currentYear) {
        isValid = false;
        setError(
            'selectedInstitute.passOutYear',
            ['Student completion year must be current or future'],
            dispatch
        );
        }

        const durationYears = getProgramDurationInYears(state?.data?.selectedInstitute?.programId, state);
        if (durationYears != null) {
            const minStart = getStudentStartYearMin(durationYears, currentYear);
            if (start < minStart || start > currentYear) {
                isValid = false;
                setError(
                    'selectedInstitute.startYear',
                    ['Start year must be within the program duration'],
                    dispatch
                );
            }
        }
    }

    return isValid;
};


export const handleNext = async (
    event: React.SyntheticEvent<HTMLButtonElement>,
    state: State,
    dispatch: (dispatchEvent: DispatchEvent) => void,
    globalState: any,
    globalDispatch: (dispatchEvent: DispatchEvent) => void,
    sessionState: any,
    sessionDispatch: (dispatchEvent: DispatchEvent) => void
) => {
    event.preventDefault();
    logger.log(state.data['profile']);
   // alert("handleNext function called");
    const customValidationPassed = await isFormValidBasedOnCustomValidation(state, dispatch);
    const isAggreeToUnivarietyClicked = isAggreeToUnivariety(state, dispatch);
    const academicYearValid = validateAcademicYears(state, dispatch);


    // const errorKeysToIgnore = getErrorsToIgnore(state);
    // const isImpersonatedSession = sessionStorage.getItem('is-impersonated-session') === 'YES';

    // if (!isImpersonatedSession && (!isFormValid(state, dispatch /*, errorKeysToIgnore*/) || !customValidationPassed)) {
    if (!isFormValid(state, dispatch /*, errorKeysToIgnore*/) || !customValidationPassed || isAggreeToUnivarietyClicked || !academicYearValid) {
        dispatch({ type: 'SHOW_ERRORS' });
        alert('Please correct the form errors!');
        return;
    }
    console.log('sessionState');
    console.log(sessionState);
    let programLevel =
        sessionState.userTypeCode == 'SUPER_ADMIN' || sessionState.userTypeCode == 'INSTITUTE_ADMIN'
            ? state.data.programLevel
            : sessionState.programLevel;
    let postProgramCompletionStatusCode = state?.data?.selectedInstitute?.postProgramCompletionStatusCode;

    if (state?.data?.selectedInstitute?.postProgramCompletionStatusCode === 'WORKING') {
        postProgramCompletionStatusCode = state?.data?.selectedInstitute?.postPorogramNatureOfWork;
    }

    if (postProgramCompletionStatusCode == 'WENT_FOR_HIGHER_STUDIES') {
        // postProgramCompletionStatusCode = state?.data?.selectedInstitute?.programId == "K12" ? "JOINED_UG" : "JOINED_PG";
        // postProgramCompletionStatusCode = sessionState.programLevel == "K12" ? "JOINED_UG" : "JOINED_PG";
        switch (programLevel) {
            // case '1':
            // case '2':
            // case '3':
            // case '4':
            // case '5':
            // case '6':
            // case '7':
            // case '8':
            // case '9':
            // case '10':
            // case '11':
            //     postProgramCompletionStatusCode = 'JOINED_SCHOOL';
            // break;
            case '12':
                postProgramCompletionStatusCode = 'JOINED_UG';
                break;

            case 'UG':
                postProgramCompletionStatusCode = 'JOINED_PG';
                break;

            case 'PG':
                postProgramCompletionStatusCode = 'JOINED_DOCTORAL';
                break;

            case 'Doctoral':
                postProgramCompletionStatusCode = 'JOINED_DOCTORAL';
                break;

            default:
                if (parseInt(programLevel) >= 1 && parseInt(programLevel) < 12) {
                    postProgramCompletionStatusCode = 'JOINED_SCHOOL';
                }
                break;
        }
    }

    let selectedInstitute = { ...state?.data?.selectedInstitute };
    // selectedInstitute.postProgramCompletionStatusCode = postProgramCompletionStatusCode; // Change #VP
    selectedInstitute.postProgramCompletionStatusCode =
    postProgramCompletionStatusCode === "WORKING"
    ? state?.data?.selectedInstitute?.postPorogramNatureOfWork
    : postProgramCompletionStatusCode;


    // if outcomes is for ug/pg then set the start and end date
    console.log(selectedInstitute);
    let underGradCollegeJoined = { ...state?.data?.underGradCollegeJoined };
    let postGradCollegeJoined = { ...state?.data.postGradCollegeJoined };
    interface PostGradCollege {
        id: number;
        startYear: any;
        endYear: any;
        universityId: number;
        // Add other properties as needed
    }

    let postGradCollegeArray: PostGradCollege[] = [];

    for (const college of Object.values(postGradCollegeJoined)) {
        // Check if college is truthy and an object before spreading
        if (college && typeof college === 'object') {
            const mutableCollege: PostGradCollege = { ...(college as PostGradCollege) };
            postGradCollegeArray.push(mutableCollege);
        }
    }

for (const key in postGradCollegeJoined) {
    const college = postGradCollegeJoined[key];

    if (college?.universityId == sessionState.instituteId) {
        postGradCollegeJoined[key] = {
            ...college,
            startYear: selectedInstitute.startYear,
            endYear: selectedInstitute.passOutYear,
        };
    }
}

    if (sessionState.programLevel === 'UG' && sessionState.instituteId == underGradCollegeJoined.universityId) {
        underGradCollegeJoined.startYear = selectedInstitute.startYear;
        underGradCollegeJoined.endYear = selectedInstitute.passOutYear;
    }

    const param: any = {
        id: state.routeInfo.id,
        selectedInstitute: {...selectedInstitute, profileStatusCode: selectedInstitute.profileStatusCode == null ? 'basic-info-complete' : selectedInstitute.profileStatusCode},
        userInstitutes: state?.data?.userInstitutes,
        profile: {
            ...state?.data?.profile,
            contactEmailId: state?.data?.profile.contactEmailId,
            contactMobileNumber: state?.data?.profile.contactMobileNumber,
            contactMobileCountryCode: state?.data?.profile.contactMobileCountryCode,
        },
        user: {
            ...state?.data?.user,
            agreeToUnivariety: state?.data?.user?.agreeToUnivariety ?? true,
        },
        socialLinks:state?.data?.socialLinks,
        // alumniBreak: state?.data?.alumniBreak,
    };

    // Conditionally include underGradColleges parameter if underGradCollegeJoined is not null or empty
    if (!isEmpty(state?.data?.underGradCollegeJoined)) {
        param.underGradColleges = state?.data.underGradColleges;
        param.underGradCollegeJoined = underGradCollegeJoined;
        param.underGradCollegeApplied = state?.data.underGradCollegeApplied;
    }
    if (!isEmpty(state?.data?.postGradCollege)) {
        param.postGradCollege = state?.data.postGradCollege; // Assign postGradCollegeArray instead of postGradCollegeJoined
        param.postGradCollegeJoined = postGradCollegeJoined;
        param.postGradCollegeApplied = state?.data?.postGradCollegeApplied;
    }

    type PostProgramCompletionStatusCode = keyof typeof postProgramCompletionRedirects;
    const postProgramCompletionRedirects = {
        APPEARED_FOR_COMPETITIVE_EXAM: (user_id: any) => `/alumni/${user_id}/entrance-test`,
        ENTREPRENEURSHIP: (user_id: any, userTypeCode: any) =>
            userTypeCode === 'STUDENT' ? `/alumni/${user_id}/job-preferences` : `/alumni/${user_id}/entrepreneurship-outcome`,
        IN_DEFENCE: (user_id: any) => `/alumni/${user_id}/defence-experience-outcome`,
        TEACHING_AND_RESEARCH: (user_id: any) => `/alumni/${user_id}/teaching-experience-outcome`,
        PROFESSIONAL_PRACTICE: (user_id: any) => `/alumni/${user_id}/professional-practice-experience-outcome`,
        SELF_EMPLOYED_AND_FREELANCING: (user_id:any) => `/alumni/${user_id}/freelancing-experience-outcome`,
        JOINED_COMPANY: (user_id: any) => `/alumni/${user_id}/job-outcome`,
        WENT_FOR_HIGHER_STUDIES: (user_id: any, userTypeCode: string, programLevel: any) => {
            console.log('programLevel' + programLevel);
            switch (programLevel) {
                case '12':
                    return `/alumni/${user_id}/ug/outcome`;
                case 'UG':
                case 'PG':
                case 'Doctoral':
                    return `/alumni/${user_id}/pg/outcome`;
                default:
                    const schoolArr = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
                    return schoolArr.includes(programLevel) ? `/alumni/${user_id}/student-school` : '';
            }
        },
        TOOK_BREAK: (user_id: any) => `/alumni/${user_id}/student-took-a-break`,
        TAKE_BREAK: (user_id: any) => `/alumni/${user_id}/student-took-a-break`,
        JOB_HUNTING: (user_id: any) => `/alumni/${user_id}/job-preferences/outcome`,
        HIGHER_STUDIES: (user_id: any, userTypeCode: any, programLevel: any) => {
            switch (programLevel) {
                case '12':
                    return `/alumni/${user_id}/ug-education-preference/outcome`;
                case 'UG':
                case 'PG':
                    return `/alumni/${user_id}/pg-education-preference/outcome`;
                default:
                    return parseInt(programLevel) >= 1 && parseInt(programLevel) < 12 ? `/alumni/${user_id}/student-preference/outcome` : '';
            }
        },
        COMPETITIVE_EXAM_PREPARATION: (user_id: any) => `/alumni/${user_id}/entrance-exam`,
    };


    StudentService.pageSave(state.routeInfo.id, state.routeInfo.pageName, StudentService.toStudentEntity(param)).then((response) => {
        if (response?.status === 200) {
            // Send event to Moengage my basics
            sendMoEngageEvent(state?.data);

            let sessionStateData = { ...sessionState, pursuingEducationLevelCode: state?.data?.profile?.pursuingEducationLevelCode , profileStatusCode: selectedInstitute.profileStatusCode == null ? 'basic-info-complete' : selectedInstitute.profileStatusCode};
            // let programLevel = sessionState?.programLevel;
            let programLevel = (sessionState.userTypeCode == 'SUPER_ADMIN' || sessionState.userTypeCode == 'INSTITUTE_ADMIN')
            ? state.data.programLevel
            : sessionState.programLevel;
            sessionDispatch({ type: 'SET', payload: sessionStateData });
            const user_id = state.routeInfo.id;
            // let redirectUrl = '';
            // switch (state?.data?.selectedInstitute?.postProgramCompletionStatusCode) {
            //     case 'APPEARED_FOR_COMPETITIVE_EXAM':
            //         redirectUrl = `/alumni/${user_id}/entrance-test`;
            //         break;

            //     case 'ENTREPRENEURSHIP':
            //         redirectUrl =
            //             state?.data?.selectedInstitute?.userTypeCode == 'STUDENT'
            //                 ? `/alumni/${user_id}/job-preferences`
            //                 : `/alumni/${user_id}/entrepreneurship`;
            //         break;

            //     case 'JOINED_COMPANY':
            //         redirectUrl = `/alumni/${user_id}/job-outcome`;
            //         break;

            //     case 'WENT_FOR_HIGHER_STUDIES':
            //         // switch ( state?.data?.profile?.pursuingEducationLevelCode ) {
            //         switch (programLevel) {
            //             case '12':
            //                 redirectUrl = `/alumni/${user_id}/ug`;
            //                 break;
            //             case 'UG':
            //             case 'PG':
            //             case 'Doctoral':
            //                 redirectUrl = `/alumni/${user_id}/pg`;
            //                 break;
            //             default:
            //                 let schoolArr = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
            //                 if (schoolArr.includes(programLevel)) {
            //                     redirectUrl = `/alumni/${user_id}/student-school`;
            //                 }
            //                 break;
            //         }
            //         break;

            //     case 'TOOK_BREAK':
            //     case 'TAKE_BREAK':
            //         redirectUrl = `/alumni/${user_id}/student-took-a-break`;
            //         break;

            //     case 'JOB_HUNTING':
            //         redirectUrl = `/alumni/${user_id}/job-preferences`;
            //         break;

            //     case 'HIGHER_STUDIES':
            //         switch (state?.data?.profile?.pursuingEducationLevelCode) {
            //             case '12':
            //                 redirectUrl = `/alumni/${user_id}/ug-education-preference`;
            //                 break;
            //             case 'UG':
            //             case 'PG':
            //                 redirectUrl = `/alumni/${user_id}/pg-education-preference`;
            //                 break;
            //             default:
            //                 if (parseInt(programLevel) >= 1 && parseInt(programLevel) < 12) {
            //                     redirectUrl = `/alumni/${user_id}/student-preference`;
            //                 }
            //         }
            //         break;

            //     case 'COMPETITIVE_EXAM_PREPARATION':
            //         redirectUrl = `/alumni/${user_id}/entrance-exam`;
            //         break;
            // }

            //new added instead o switch handling with array
            // const postProgramCompletionStatusCode = state?.data?.selectedInstitute
            //     ?.postProgramCompletionStatusCode as PostProgramCompletionStatusCode; //change #VP
          const postProgramCompletionStatusCode =
            (
                state?.data?.selectedInstitute?.postProgramCompletionStatusCode === "WORKING"
                ? state?.data?.selectedInstitute?.postPorogramNatureOfWork
                : state?.data?.selectedInstitute?.postProgramCompletionStatusCode
            ) as keyof typeof postProgramCompletionRedirects;

            console.log("postProgramCompletionStatusCode",postProgramCompletionStatusCode)

            let redirectUrl = postProgramCompletionRedirects[postProgramCompletionStatusCode]
                ? postProgramCompletionRedirects[postProgramCompletionStatusCode](
                      user_id,
                      state?.data?.selectedInstitute?.userTypeCode,
                      programLevel
                  )
                : '';
            
            // temp fix for redirecting to outcome page if postProgramCompletionStatusCode is empty or null or undefined
            if (redirectUrl === '') {
                redirectUrl = `/alumni/${user_id}/outcome`;
            }
            console.log("redirectUrl",redirectUrl)

            let profileStatusCode = selectedInstitute.profileStatusCode == null ? 'basic-info-complete' : selectedInstitute.profileStatusCode;
            let localStorageUser = JSON.parse(localStorage.getItem('user') as string);
            localStorageUser.profileStatusCode = profileStatusCode;
            localStorage.setItem('user', JSON.stringify(localStorageUser));

            let sessionString = sessionStorage.getItem('user') as string;
            if(sessionString){
                let sessionStorageUser = JSON.parse(sessionString);
                sessionStorageUser.profileStatusCode = profileStatusCode;
                sessionStorage.setItem('user', JSON.stringify(sessionStorageUser));
            }
            

            // state.actions['REDIRECT'](redirectUrl);
            navigateToNextStep(AlumniProfileEditScreens.PROGRAM_BASIC_INFORMATION, state, dispatch, sessionState, sessionDispatch);
        }
    });
};

export const isFormValidBasedOnCustomValidation = async (state: State, dispatch: any) => {
    let isFormValid = true;

    let postProgramCompletionStatusCode =
        state?.data?.selectedInstitute?.postProgramCompletionStatusCode;

    setError(`selectedInstitute.postProgramCompletionStatusCode`, [], dispatch);

    // if (isEmpty(postProgramCompletionStatusCode)) {
    //     isFormValid = false;
    //     setError(
    //         'selectedInstitute.postProgramCompletionStatusCode',
    //         [`Please enter "Post Completion of the program"`],
    //         dispatch
    //     );
    // }

    // 🔥 SOCIAL LINKS VALIDATION
    const socialLinks = state?.data?.socialLinks || [];

    socialLinks.forEach((item: any, index: number) => {
        const url = item?.link?.trim();
        const network = item?.socialNetworkCode;

        // Clear previous error
        setError(`socialLinks.${index}.link`, [], dispatch);

        if (!isEmpty(url)) {
            // Generic URL validation
            const urlPattern =
                /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(.+)?$/;

            if (!urlPattern.test(url)) {
                isFormValid = false;
                setError(
                    `socialLinks.${index}.link`,
                    ['Please enter a valid URL'],
                    dispatch
                );
                return;
            }

            // Platform specific validation
            const platformPatterns: any = {
                LINKEDIN: /linkedin\.com\/(in|company|school|pub)\//i,
                FACEBOOK: /facebook\.com\//i,
                INSTAGRAM: /instagram\.com\//i,
                TWITTER: /(twitter\.com|x\.com)\//i,
            };

            if (
                platformPatterns[network] &&
                !platformPatterns[network].test(url)
            ) {
                isFormValid = false;
                setError(
                    `socialLinks.${index}.link`,
                    [`Please enter a valid ${network.toLowerCase()} profile URL`],
                    dispatch
                );
            }
        }
    });

    return isFormValid;
};

// const getErrorsToIgnore = (state: State) => {
//     if (state.data.profile.passOutYear > new Date().getFullYear() - 2 && state.data.profile.didYouTakeGapAfter12 === true)
//         return ['profile.educationOrEmploymentCurrentStatusCode'];
// };

const sendMoEngageEvent = async (user: any) => {
    try {
        const response = await axios.post(`${process.env.REACT_APP_MOENGAGE_API}/moengage/moengage-program-info`, {
            userInfo: user,
        });
        // console.log('Response:', response.data);
    } catch (error) {
        console.error('Error fetching data:', error);
    }
    return false;
};
